import { AbstractMesh, Color3, LinesMesh, Matrix, Mesh, MeshBuilder, PBRMaterial, Quaternion, Scene, StandardMaterial, TransformNode, Vector3, VertexBuffer } from "@babylonjs/core";
import type { GameAssetManager } from "../core/AssetManager";
import { PONTIANAK_AUTHORED_FORWARD, type GhostDefinition } from "./GhostDefinitions";
import { GhostState } from "./GhostStateMachine";
import { shortestYawStep, yawFromVelocity } from "./GhostFacing";
import { PontianakPoseController, type PontianakActionPose } from "./PontianakPoseController";

// One authored gameplay tolerance, applied to the transformed bounds of each
// loaded Polong instance. The resulting radius is never shared between instances.
export const POLONG_HIT_RADIUS_MULTIPLIER = 1.8;
export const PONTIANAK_BOB_AMPLITUDE = 0.045;
export const PONTIANAK_BOB_FREQUENCY_HZ = 0.72;
export const PONTIANAK_LATERAL_SWAY = 0.018;
export const PONTIANAK_ESCAPE_RISE = 2.4;
export const PONTIANAK_ESCAPE_RISE_SPEED = 1.4;
export const PENANGGAL_COLLIDER_PADDING = 0.08;
export const PENANGGAL_COLLIDER_MIN_RADIUS = 0.32;
export const PENANGGAL_COLLIDER_MAX_RADIUS = 0.52;
export const PENANGGAL_ALPHA_CUTOFF = 0.18;
export function polongHitRadiusFromBounds(size: Vector3, multiplier = POLONG_HIT_RADIUS_MULTIPLIER): number {
  return Math.max(size.x, size.y, size.z) * 0.5 * multiplier;
}

export function configurePenanggalMaterial(material:PBRMaterial):void{
  material.useAlphaFromAlbedoTexture=true;
  material.transparencyMode=PBRMaterial.PBRMATERIAL_ALPHATESTANDBLEND;
  material.alphaCutOff=PENANGGAL_ALPHA_CUTOFF;
  material.needDepthPrePass=true;
  material.separateCullingPass=true;
  material.backFaceCulling=false;
  material.twoSidedLighting=true;
}

interface VisibleBounds { center: Vector3; size: Vector3 }

export class ConfiguredGhostVisual {
  readonly root: TransformNode;
  readonly facingRoot: TransformNode;
  readonly collider: Mesh;
  readonly baitAttachment: TransformNode;
  readonly ready: Promise<boolean>;
  readonly height: number;
  private readonly animationRoot: TransformNode;
  private readonly modelAxisCorrection: TransformNode;
  private readonly placeholder: Mesh;
  private importedModel?: TransformNode;
  private handAnchorSource?:TransformNode;
  private headAnchorSource?:TransformNode;
  private frontAnchorSource?:TransformNode;
  private poseController?:PontianakPoseController;
  private readonly localVisibleBounds = new Map<AbstractMesh, { minimum: Vector3; maximum: Vector3 }>();
  private measuredPolongHitRadius = 0;
  private measuredPenanggalHitRadius = 0;
  private time = 0;
  private reactionTimer = 0;
  private wetTimer = 0;
  private fearTimer = 0;
  private targetYaw = 0;
  private actualMovement=Vector3.Zero();
  private actualSpeed=0;
  private motionBlend=0;
  private pontianakEscapeRise=0;
  private pocongPlatformHeight=0;
  private pocongPlatformTarget=0;
  private penanggalHoverHeight=0;
  private lastOrientationWriter="spawn";
  private diagnosticsEnabled=false;
  private movementDirectionLine?:LinesMesh;
  private visualForwardLine?:LinesMesh;
  private orientationAxes?:LinesMesh;
  private readonly handWorldRotation=Quaternion.Identity();
  private readonly facingWorldRotation=Quaternion.Identity();
  private readonly inverseFacingRotation=Quaternion.Identity();
  private readonly attachmentRotation=Quaternion.Identity();
  private readonly transformScale=Vector3.One();
  private readonly transformTranslation=Vector3.Zero();
  private readonly inverseFacingMatrix=Matrix.Identity();
  private readonly handLocalPosition=Vector3.Zero();

  constructor(scene: Scene, private readonly assets: GameAssetManager, readonly definition: GhostDefinition) {
    this.height = definition.modelScale;
    this.penanggalHoverHeight=definition.hoverHeight;
    this.root = new TransformNode(`${definition.id}-root`, scene);
    this.facingRoot = new TransformNode(`${definition.id}-facing`, scene); this.facingRoot.parent = this.root;
    this.animationRoot = new TransformNode(`${definition.id}-animation-root`, scene); this.animationRoot.parent = this.facingRoot;
    this.modelAxisCorrection = new TransformNode(`${definition.id}-model-axis-correction`, scene);this.modelAxisCorrection.parent=this.animationRoot;
    this.modelAxisCorrection.rotation.set(definition.modelOrientation.x,definition.modelOrientation.y,definition.modelOrientation.z);
    this.baitAttachment = new TransformNode(`${definition.id}-bait-attachment`, scene); this.baitAttachment.parent = this.facingRoot;
    const baitFallback=definition.gameplayGeometry?.baitFallback;
    this.baitAttachment.position.set(baitFallback?.x??0.2,baitFallback?.y??definition.hoverHeight+definition.modelScale*0.62,baitFallback?.z??0.24);

    this.placeholder = definition.id === "polong" || definition.id === "penanggal"
      ? MeshBuilder.CreateIcoSphere("polong-loading-placeholder", { radius: definition.modelScale * 0.34, subdivisions: 2 }, scene)
      : MeshBuilder.CreateCapsule("pontianak-loading-placeholder", { height: definition.modelScale, radius: 0.25, tessellation: 8 }, scene);
    this.placeholder.parent = this.animationRoot;
    this.placeholder.position.y = definition.hoverHeight + definition.modelScale * 0.5;
    const material = new StandardMaterial(`${definition.id}-placeholder-material`, scene);
    material.diffuseColor = definition.id === "polong" ? new Color3(1, 0.25, 0.03) : new Color3(0.68, 0.72, 0.7);
    material.emissiveColor = definition.id === "polong" ? new Color3(0.55, 0.09, 0.01) : new Color3(0.035, 0.055, 0.05);
    this.placeholder.material = material; this.placeholder.isPickable = false;

    this.collider = definition.id === "polong"
      ? MeshBuilder.CreateSphere(`${definition.id}-capture-collider`, { diameter: 2, segments: 12 }, scene)
      : MeshBuilder.CreateCapsule(`${definition.id}-capture-collider`, { height: definition.gameplayGeometry?.colliderHeight??definition.modelScale, radius: definition.gameplayGeometry?.colliderRadius??0.28, tessellation: 8 }, scene);
    this.collider.parent = this.root;
    this.collider.position.y = definition.hoverHeight + (definition.gameplayGeometry?.colliderCenterY??definition.modelScale*0.5);
    this.collider.visibility = 0; this.collider.isPickable = true;
    this.collider.metadata = { ghost: true, ghostId: definition.id };
    if (definition.id === "polong") this.syncPolongHitSphere();
    this.ready = this.loadModel();
  }

  update(deltaSeconds: number, state: GhostState): void {
    this.time += deltaSeconds;
    this.reactionTimer = Math.max(0, this.reactionTimer - deltaSeconds);
    this.wetTimer = Math.max(0, this.wetTimer - deltaSeconds);
    this.fearTimer = Math.max(0,this.fearTimer-deltaSeconds);
    const moving = [GhostState.ROAMING, GhostState.INVESTIGATING, GhostState.ESCAPING, GhostState.AGGRESSIVE, GhostState.INTERACTING].includes(state);
    if(this.definition.id==="pontianak"){
      const targetBlend=moving?1:0;
      this.motionBlend+=(targetBlend-this.motionBlend)*Math.min(1,deltaSeconds*2.4);
      const captureScale=state===GhostState.CAPTURE_READY?.78:1;
      const phase=this.time*Math.PI*2;
      const bob=Math.sin(phase*PONTIANAK_BOB_FREQUENCY_HZ)*PONTIANAK_BOB_AMPLITUDE*captureScale;
      const sway=PONTIANAK_LATERAL_SWAY*(.55+this.motionBlend*.45);
      const escapeRiseTarget=state===GhostState.ESCAPING||state===GhostState.HIDING?PONTIANAK_ESCAPE_RISE:0;
      const escapeRiseDelta=escapeRiseTarget-this.pontianakEscapeRise;
      this.pontianakEscapeRise+=Math.sign(escapeRiseDelta)*Math.min(Math.abs(escapeRiseDelta),PONTIANAK_ESCAPE_RISE_SPEED*Math.max(0,deltaSeconds));
      this.animationRoot.position.set(Math.sin(phase*.27)*sway,this.definition.hoverHeight+this.pontianakEscapeRise+bob,Math.cos(phase*.23)*sway*.45);
      this.animationRoot.rotation.x=(state===GhostState.INTERACTING?.035:0)+Math.sin(phase*.31)*.012;
      this.animationRoot.rotation.y=state===GhostState.CAPTURING?this.time*8:0;
      this.animationRoot.rotation.z=this.reactionTimer>0?Math.sin(this.reactionTimer*34)*.1:Math.sin(phase*.29)*.015;
      this.animationRoot.scaling.setAll(1);
      this.poseController?.update(this.time,state);
    }else if(this.definition.id==="pocong"){
      this.pocongPlatformHeight+=(this.pocongPlatformTarget-this.pocongPlatformHeight)*(1-Math.exp(-Math.max(0,deltaSeconds)*7));
      const bounce=state===GhostState.INTERACTING?Math.abs(Math.sin(this.time*4.8))*.72:state===GhostState.AGGRESSIVE?Math.abs(Math.sin(this.time*6.8))*.3:moving?Math.abs(Math.sin(this.time*4.5))*.18:0;
      this.animationRoot.position.set(0,this.pocongPlatformHeight+bounce,0);this.animationRoot.rotation.x=state===GhostState.RECOVERING?Math.PI*.48:state===GhostState.AGGRESSIVE?-.12:0;this.animationRoot.rotation.z=this.reactionTimer>0?Math.sin(this.reactionTimer*30)*.16:0;this.animationRoot.scaling.setAll(1);
    }else if(this.definition.id==="penanggal"){
      const targetHover=state===GhostState.CAPTURE_READY?.58:this.definition.hoverHeight;this.penanggalHoverHeight+=(targetHover-this.penanggalHoverHeight)*(1-Math.exp(-Math.max(0,deltaSeconds)*4));const evasive=state===GhostState.AGGRESSIVE?.2:.12;this.animationRoot.position.set(Math.sin(this.time*1.7)*evasive,this.penanggalHoverHeight+Math.sin(this.time*2.2)*(state===GhostState.CAPTURE_READY?.05:.14),Math.cos(this.time*1.35)*evasive);this.animationRoot.rotation.y=state===GhostState.CAPTURING?this.time*8:Math.sin(this.time*.8)*.18;this.animationRoot.rotation.z=this.reactionTimer>0?Math.sin(this.reactionTimer*34)*.2:Math.sin(this.time*1.1)*.05;this.animationRoot.scaling.setAll(1);
    }else{
      const baseHover = state === GhostState.CAPTURE_READY ? this.definition.hoverHeight * 0.45 : this.definition.hoverHeight;
      const bob = this.definition.movementType === "floating" ? Math.sin(this.time * 2.6) * 0.11 : moving ? Math.abs(Math.sin(this.time * 5.5)) * 0.025 : Math.sin(this.time * 1.5) * 0.012;
      const candleOrbit=this.definition.id==="polong"&&state===GhostState.INTERACTING?0.22:0;
      this.animationRoot.position.set(Math.cos(this.time*0.72)*candleOrbit,baseHover+bob,Math.sin(this.time*0.72)*candleOrbit);
      this.animationRoot.rotation.x = state === GhostState.INTERACTING ? 0.12 : 0;
      this.animationRoot.rotation.y = state === GhostState.IDLE ? Math.sin(this.time * 0.55) * 0.2 : state === GhostState.CAPTURING ? this.time * 8 : 0;
      this.animationRoot.rotation.z = this.reactionTimer > 0 ? Math.sin(this.reactionTimer * 34) * 0.12 : state === GhostState.CAPTURE_READY ? Math.sin(this.time * 9) * 0.08 : 0;
      const flicker = this.wetTimer > 0 ? 0.78 + Math.sin(this.time * 28) * 0.18 : this.fearTimer>0?0.82+Math.sin(this.time*19)*0.13:1;
      this.animationRoot.scaling.set(flicker, this.wetTimer > 0 ? 1.08 - flicker * 0.08 : 1, flicker);
    }
    if(this.definition.id!=="polong"&&this.definition.id!=="penanggal")this.collider.position.set(this.animationRoot.position.x,this.animationRoot.position.y+(this.definition.gameplayGeometry?.colliderCenterY??this.definition.modelScale*0.5),this.animationRoot.position.z);
    if(this.definition.id==="polong")this.syncPolongHitSphere();
    if(this.definition.id==="penanggal")this.syncPenanggalCollider();
    this.syncHandAttachment();
    this.updateDiagnosticGeometry();
  }

  faceActualMovement(movement: Vector3, deltaSeconds: number): void {
    const horizontal = new Vector3(movement.x, 0, movement.z);
    this.actualMovement.copyFrom(horizontal);this.actualSpeed=deltaSeconds>0?horizontal.length()/deltaSeconds:0;
    if (horizontal.lengthSquared() < 0.0000001) return;
    this.targetYaw = yawFromVelocity(horizontal);
    this.facingRoot.rotation.y = shortestYawStep(this.facingRoot.rotation.y, this.targetYaw, deltaSeconds * Math.PI * 2.2);
    this.lastOrientationWriter="world-displacement";
    this.updateDiagnosticGeometry();
  }

  lookToward(target: Vector3, amount = 0.12): void {
    this.targetYaw = Math.atan2(target.x - this.root.position.x, target.z - this.root.position.z);
    this.facingRoot.rotation.y = shortestYawStep(this.facingRoot.rotation.y, this.targetYaw, amount);
    this.lastOrientationWriter="bait-look";
    this.updateDiagnosticGeometry();
  }

  get movementDiagnostics():{movement:Vector3;speed:number;targetYaw:number;currentYaw:number;axisCorrection:number;angularError:number;visualForward:Vector3;forwardSource:string;boundsSize?:Vector3;footWorldY?:number;handSource?:string;animationNames:readonly string[];lastWriter:string;floatOffset:Vector3;activePose:string;animationPlayback:string;skeletonBoneCount:number;armsLowered:boolean;armDrop:number}{
    const authoredForward=this.definition.id==="pontianak"?PONTIANAK_AUTHORED_FORWARD:Vector3.Forward();
    let visualForward:Vector3;
    let forwardSource=this.modelAxisCorrection.name;
    if(this.definition.id==="pontianak"&&this.headAnchorSource&&!this.headAnchorSource.isDisposed()&&this.frontAnchorSource&&!this.frontAnchorSource.isDisposed()){
      this.headAnchorSource.computeWorldMatrix(true);this.frontAnchorSource.computeWorldMatrix(true);
      visualForward=this.frontAnchorSource.getAbsolutePosition().subtract(this.headAnchorSource.getAbsolutePosition());
      visualForward.y=0;
      if(visualForward.lengthSquared()>.000001){visualForward.normalize();forwardSource=this.frontAnchorSource.name;}
      else{this.modelAxisCorrection.computeWorldMatrix(true);visualForward=Vector3.TransformNormal(authoredForward,this.modelAxisCorrection.getWorldMatrix()).normalize();}
    }else{
      this.modelAxisCorrection.computeWorldMatrix(true);visualForward=Vector3.TransformNormal(authoredForward,this.modelAxisCorrection.getWorldMatrix()).normalize();
    }
    const bounds=this.measureVisibleBounds();
    const animationNames=(this.importedModel?.metadata?.animationNames as string[]|undefined)??[];
    const pose=this.poseController?.diagnostics;
    return{movement:this.actualMovement.clone(),speed:this.actualSpeed,targetYaw:this.targetYaw,currentYaw:this.facingRoot.rotation.y,axisCorrection:this.definition.modelOrientation.y,angularError:Math.atan2(Math.sin(this.targetYaw-this.facingRoot.rotation.y),Math.cos(this.targetYaw-this.facingRoot.rotation.y)),visualForward,forwardSource,boundsSize:bounds?.size,footWorldY:bounds?bounds.center.y-bounds.size.y*.5:undefined,handSource:this.handAnchorSource?.name,animationNames,lastWriter:this.lastOrientationWriter,floatOffset:this.animationRoot.position.clone(),activePose:pose?.activePose??"none",animationPlayback:pose?.playback??(this.definition.animationConfiguration.playEmbedded?"embedded-loop":"stopped"),skeletonBoneCount:pose?.skeletonBoneCount??Number(this.importedModel?.metadata?.skeletonBoneCount??0),armsLowered:pose?.armsLowered??false,armDrop:pose?.armDrop??0};
  }

  get visibleCenter():Vector3{return this.measureVisibleBounds().center;}
  get visibleBoundsCenter():Vector3{return this.measureVisibleBounds().center;}
  get visibleBoundsSize():Vector3{return this.measureVisibleBounds().size;}
  get hitRadius():number|undefined{return this.definition.id==="polong"?this.measuredPolongHitRadius:this.definition.id==="penanggal"?this.measuredPenanggalHitRadius:undefined;}
  get headWorldPosition():Vector3{if(this.headAnchorSource&&!this.headAnchorSource.isDisposed()){this.headAnchorSource.computeWorldMatrix(true);return this.headAnchorSource.getAbsolutePosition().clone();}const bounds=this.measureVisibleBounds();return bounds.center.add(new Vector3(0,bounds.size.y*.55,0));}

  setDiagnosticsEnabled(enabled:boolean):void{
    if(!import.meta.env.DEV||this.definition.id!=="pontianak")return;
    this.diagnosticsEnabled=enabled;
    if(enabled&&!this.movementDirectionLine)this.createDiagnosticGeometry();
    this.movementDirectionLine?.setEnabled(enabled);
    this.visualForwardLine?.setEnabled(enabled);
    this.orientationAxes?.setEnabled(enabled);
    if(enabled)this.updateDiagnosticGeometry();
  }

  hit(wet = false): void { this.reactionTimer = 0.55; if (wet) this.wetTimer = 0.85; }
  setPocongPlatformHeight(offset:number):void{if(this.definition.id==="pocong"&&Number.isFinite(offset))this.pocongPlatformTarget=Math.max(0,offset);}
  fear():void{this.fearTimer=1.15;}
  setPontianakActionPose(pose:PontianakActionPose,progress=0):void{this.poseController?.setActionPose(pose,progress);}

  private createDiagnosticGeometry():void{
    if(!import.meta.env.DEV||this.definition.id!=="pontianak"||this.movementDirectionLine)return;
    const origin=new Vector3(0,this.height*1.1,0);
    this.movementDirectionLine=MeshBuilder.CreateLines("debug-pontianak-movement",{points:[origin,origin.clone()],updatable:true},this.root.getScene());
    this.movementDirectionLine.parent=this.root;this.movementDirectionLine.color=Color3.Green();this.movementDirectionLine.isPickable=false;
    this.visualForwardLine=MeshBuilder.CreateLines("debug-pontianak-visual-forward",{points:[origin,origin.clone()],updatable:true},this.root.getScene());
    this.visualForwardLine.parent=this.root;this.visualForwardLine.color=Color3.Blue();this.visualForwardLine.isPickable=false;
    this.orientationAxes=MeshBuilder.CreateLineSystem("debug-pontianak-world-axes",{lines:[
      [origin,origin.add(new Vector3(1,0,0))],
      [origin,origin.add(new Vector3(-1,0,0))],
      [origin,origin.add(new Vector3(0,0,1))],
      [origin,origin.add(new Vector3(0,0,-1))],
    ]},this.root.getScene());
    this.orientationAxes.parent=this.root;this.orientationAxes.color=Color3.White();this.orientationAxes.isPickable=false;
  }

  private updateDiagnosticGeometry():void{
    if(!import.meta.env.DEV||!this.diagnosticsEnabled||!this.movementDirectionLine||!this.visualForwardLine)return;
    const origin=new Vector3(0,this.height*1.1,0);
    const movement=this.actualMovement.lengthSquared()>.0000001?this.actualMovement.normalizeToNew():Vector3.Zero();
    const forward=this.movementDiagnostics.visualForward;
    MeshBuilder.CreateLines("debug-pontianak-movement",{points:[origin,origin.add(movement.scale(1.25))],instance:this.movementDirectionLine},this.root.getScene());
    MeshBuilder.CreateLines("debug-pontianak-visual-forward",{points:[origin,origin.add(forward.scale(1.25))],instance:this.visualForwardLine},this.root.getScene());
  }

  private measureVisibleBounds(): VisibleBounds {
    const meshes = this.importedModel && !this.importedModel.isDisposed()
      ? this.importedModel.getChildMeshes(false)
      : this.placeholder.isDisposed() ? [] : [this.placeholder];
    let minimum = new Vector3(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY);
    let maximum = new Vector3(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY);
    for (const mesh of meshes) {
      mesh.computeWorldMatrix(true);
      if (this.definition.id === "pontianak") {
        // Skinned Kuntilanak vertices are stored in bind space. Babylon's
        // bounding box is the same authoritative space used by AssetManager
        // to normalize and bottom-align the rig; transforming raw positions
        // here would incorrectly report her feet below the terrain.
        const bounds = mesh.getBoundingInfo().boundingBox;
        minimum = Vector3.Minimize(minimum, bounds.minimumWorld);
        maximum = Vector3.Maximize(maximum, bounds.maximumWorld);
        continue;
      }
      const local = this.getBaseGeometryBounds(mesh);
      if (local) {
        // GLB conversion roots can be Mesh instances without geometry whose
        // aggregate bounds include large morph-target deltas. Only authored
        // vertex geometry represents the visible fire body.
        for (const x of [local.minimum.x, local.maximum.x]) for (const y of [local.minimum.y, local.maximum.y]) for (const z of [local.minimum.z, local.maximum.z]) {
          const world = Vector3.TransformCoordinates(new Vector3(x, y, z), mesh.getWorldMatrix());
          minimum = Vector3.Minimize(minimum, world);
          maximum = Vector3.Maximize(maximum, world);
        }
      }
    }
    if (meshes.length > 0 && Number.isFinite(minimum.x)) return { center: minimum.add(maximum).scale(0.5), size: maximum.subtract(minimum) };
    this.animationRoot.computeWorldMatrix(true);
    return {
      center: Vector3.TransformCoordinates(new Vector3(0, this.definition.modelScale * 0.5, 0), this.animationRoot.getWorldMatrix()),
      size: new Vector3(this.definition.modelScale, this.definition.modelScale, this.definition.modelScale),
    };
  }

  private getBaseGeometryBounds(mesh: AbstractMesh): { minimum: Vector3; maximum: Vector3 } | undefined {
    const cached = this.localVisibleBounds.get(mesh);
    if (cached) return cached;
    const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
    if (!positions || positions.length < 3) return undefined;
    let minimum = new Vector3(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY);
    let maximum = new Vector3(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY);
    for (let index = 0; index < positions.length; index += 3) {
      const position = new Vector3(positions[index]!, positions[index + 1]!, positions[index + 2]!);
      minimum = Vector3.Minimize(minimum, position);
      maximum = Vector3.Maximize(maximum, position);
    }
    const bounds = { minimum, maximum };
    this.localVisibleBounds.set(mesh, bounds);
    return bounds;
  }

  private syncPolongHitSphere(): void {
    const bounds = this.measureVisibleBounds();
    const radius = polongHitRadiusFromBounds(bounds.size);
    this.root.computeWorldMatrix(true);
    const inverseRoot = this.root.getWorldMatrix().clone().invert();
    this.collider.position.copyFrom(Vector3.TransformCoordinates(bounds.center, inverseRoot));
    const rootScale = Math.max(Math.abs(this.root.scaling.x), Math.abs(this.root.scaling.y), Math.abs(this.root.scaling.z), 0.0001);
    this.collider.scaling.setAll(radius / rootScale);
    this.measuredPolongHitRadius = radius;
    this.collider.metadata = {
      ...this.collider.metadata,
      visibleBodySize: bounds.size.clone(),
      hitRadiusMultiplier: POLONG_HIT_RADIUS_MULTIPLIER,
      hitRadius: radius,
    };
  }

  private syncPenanggalCollider():void{
    const bounds=this.measureVisibleBounds();
    this.root.computeWorldMatrix(true);
    const inverseRoot=this.root.getWorldMatrix().clone().invert();
    this.collider.position.copyFrom(Vector3.TransformCoordinates(bounds.center,inverseRoot));
    const rootScale=Math.max(Math.abs(this.root.scaling.x),Math.abs(this.root.scaling.y),Math.abs(this.root.scaling.z),0.0001);
    const horizontalRadius=Math.max(PENANGGAL_COLLIDER_MIN_RADIUS*rootScale,Math.min(PENANGGAL_COLLIDER_MAX_RADIUS*rootScale,Math.min(bounds.size.x,bounds.size.z)*0.5+PENANGGAL_COLLIDER_PADDING*rootScale));
    const height=Math.max(horizontalRadius*2,bounds.size.y+PENANGGAL_COLLIDER_PADDING*2*rootScale);
    const geometry=this.definition.gameplayGeometry;
    const sourceRadius=Math.max(geometry?.colliderRadius??0.38,0.001);
    const sourceHeight=Math.max(geometry?.colliderHeight??this.definition.modelScale,0.001);
    this.collider.scaling.set(horizontalRadius/sourceRadius/rootScale,height/sourceHeight/rootScale,horizontalRadius/sourceRadius/rootScale);
    this.measuredPenanggalHitRadius=Math.max(horizontalRadius,height*0.5);
    this.collider.metadata={...this.collider.metadata,visibleBodySize:bounds.size.clone(),damageRadius:this.measuredPenanggalHitRadius,navigationRadius:0.32};
  }

  private centerPenanggalImportedModel():void{
    const model=this.importedModel;if(!model||this.definition.id!=="penanggal")return;
    this.modelAxisCorrection.computeWorldMatrix(true);
    const bounds=this.measureVisibleBounds(),inverseCorrection=this.modelAxisCorrection.getWorldMatrix().clone().invert();
    const localCenter=Vector3.TransformCoordinates(bounds.center,inverseCorrection);
    const localBottom=Vector3.TransformCoordinates(bounds.center.subtract(new Vector3(0,bounds.size.y*.5,0)),inverseCorrection);
    model.position.addInPlace(new Vector3(-localCenter.x,-localBottom.y,-localCenter.z));
    model.computeWorldMatrix(true);
    for(const mesh of model.getChildMeshes(false))mesh.computeWorldMatrix(true);
  }

  private syncHandAttachment():void{
    if(!this.handAnchorSource||this.handAnchorSource.isDisposed())return;
    this.handAnchorSource.computeWorldMatrix(true);this.facingRoot.computeWorldMatrix(true);
    this.inverseFacingMatrix.copyFrom(this.facingRoot.getWorldMatrix()).invert();
    Vector3.TransformCoordinatesToRef(this.handAnchorSource.getAbsolutePosition(),this.inverseFacingMatrix,this.handLocalPosition);
    const offset=this.definition.gameplayGeometry?.handOffset;
    if(offset)this.handLocalPosition.addInPlaceFromFloats(offset.x,offset.y,offset.z);
    if(this.baitAttachment.parent!==this.facingRoot)this.baitAttachment.parent=this.facingRoot;
    this.baitAttachment.position.copyFrom(this.handLocalPosition);
    this.handAnchorSource.getWorldMatrix().decompose(this.transformScale,this.handWorldRotation,this.transformTranslation);
    this.facingRoot.getWorldMatrix().decompose(this.transformScale,this.facingWorldRotation,this.transformTranslation);
    this.facingWorldRotation.conjugateToRef(this.inverseFacingRotation);
    this.inverseFacingRotation.multiplyToRef(this.handWorldRotation,this.attachmentRotation);
    if(!this.baitAttachment.rotationQuaternion)this.baitAttachment.rotationQuaternion=Quaternion.Identity();
    this.baitAttachment.rotationQuaternion.copyFrom(this.attachmentRotation);
    this.baitAttachment.rotation.setAll(0);
  }

  private async loadModel(): Promise<boolean> {
    if (!this.definition.modelPath) return false;
    const model = await this.assets.instantiate(this.definition.modelPath, `${this.definition.id}-model`, {
      scale: this.definition.modelScale,
      normalizeBy: "height",
      rotation: Vector3.Zero(),
      playAnimations: this.definition.animationConfiguration.playEmbedded,
    });
    if (!model || this.root.isDisposed()) { model?.dispose(); return false; }
    model.parent = this.modelAxisCorrection;
    this.importedModel = model;
    const handName=this.definition.gameplayGeometry?.handNodeName;
    if(handName)this.handAnchorSource=model.getDescendants(false).find((node)=>node instanceof TransformNode&&(node.name===handName||node.name.endsWith(`-${handName}`))) as TransformNode|undefined;
    this.headAnchorSource=model.getDescendants(false).find((node)=>node instanceof TransformNode&&(node.name==="Head"||node.name.endsWith("-Head"))) as TransformNode|undefined;
    this.frontAnchorSource=model.getDescendants(false).find((node)=>node instanceof TransformNode&&(node.name==="headfront"||node.name.endsWith("-headfront"))) as TransformNode|undefined;
    if(this.definition.id==="pontianak")this.poseController=new PontianakPoseController(model);
    for (const mesh of model.getChildMeshes(false)) {
      mesh.isPickable = false;
      mesh.metadata = { ...mesh.metadata, ghostVisual: true, ghostId: this.definition.id };
      if (this.definition.id === "polong" && mesh.material && "emissiveColor" in mesh.material) {
        (mesh.material as { emissiveColor: Color3 }).emissiveColor = new Color3(0.18, 0.035, 0.005);
      }
      if(this.definition.id==="penanggal"&&mesh.material instanceof PBRMaterial)configurePenanggalMaterial(mesh.material);
    }
    this.centerPenanggalImportedModel();
    if (this.definition.id === "polong") this.syncPolongHitSphere();
    if (this.definition.id === "penanggal") this.syncPenanggalCollider();
    this.syncHandAttachment();
    this.placeholder.dispose();
    return true;
  }
}
